import React, { useContext, useState } from "react"
import { Div, Row, Col, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRightFromBracket, faTrash } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import Container from "../atoms/Container"
import ToggleChip from "../atoms/ToggleChip"
import Paragraph from "../atoms/Paragraph"
import TextInput from "../inputs/TextInput"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { DEFAULT_SPORT, withDisplayNames } from "../utils/func.utils"
import {
	isOwnSetting,
	isOwnLessonType,
	getSportLessonTypes,
	newSchoolLessonTypeID,
	resolveLessonTypes,
} from "../utils/school.utils"
import SchoolAvatar from "../school/SchoolAvatar"
import SportSelector from "../school/SportSelector"
import SchoolCourtsEditor from "../school/SchoolCourtsEditor"
import LessonTypesEditor from "../school/LessonTypesEditor"
import LessonTypeForm, {
	buildLessonTypeAdjustment,
	buildSchoolLessonType,
	draftFromLessonType,
	EMPTY_DRAFT_TYPE,
} from "../school/LessonTypeForm"
import CourtsFeeEditor from "../school/CourtsFeeEditor"
import CourtPaidByEditor from "../school/CourtPaidByEditor"
import PlayersPayToEditor from "../school/PlayersPayToEditor"
import { COURT_PAID_BY_COACH, PAID_TO_COACH } from "../utils/billing.utils"
import StepSwap from "../atoms/StepSwap"
import { useBackSteps } from "../atoms/useBackClose"

// Una sezione per volta, a tutto schermo: la modifica ha un inizio, una
// fine e una cosa sola sotto gli occhi. Aprire tutta la scheda in modifica
// lasciava l'utente a cercare cosa fosse cambiato.
const TITLES = {
	dati: "Dati del circolo",
	campi: "Campi",
	prezzi: "Tipologie di lezione",
	pagamenti: "Pagamenti",
	tariffe: "Chi paga il campo, e quanto",
}

// Le prime due sono del luogo, le altre sono condizioni del maestro: cambia
// chi può scriverle e su quale documento finiscono.
const SCHOOL_SECTIONS = ["dati", "campi"]

// Campi, prezzi e tariffe hanno una versione per ogni sport: si sceglie in
// cima e sotto si vede solo quella.
const PER_SPORT_SECTIONS = ["campi", "prezzi", "tariffe"]

// Le condizioni che ogni sezione scrive. Tornando ai valori del circolo si
// azzerano tutte quelle della sezione: una mezza eredità non vuol dire
// niente.
const SETTINGS_FIELDS = {
	// `lessonTypes` non compare qui: le tipologie proprie non sono
	// un'eredità del circolo, sono tue, e tornare al listino comune non deve
	// cancellarle
	prezzi: ["lessonPrices"],
	// `billingData` non compare qui: non è una condizione ereditata dal
	// circolo, è quello che il circolo paga a te
	pagamenti: ["availablePaymentMethods", "playersPayTo"],
	tariffe: ["courtsFee", "courtPaidBy"],
}

const initialDraft = (section, school, settings) => {
	switch (section) {
		case "dati":
			return {
				schoolName: school.schoolName ?? "",
				mapsName: school.mapsName ?? "",
				address: school.address ?? "",
			}
		case "campi":
			return { courts: school.courts ?? {} }
		case "prezzi":
			// quali lezioni si fanno, a chi si fatturano e a quanto: sono la
			// stessa decisione presa da tre lati, e si toccano insieme —
			// togliere un tipo vuol dire togliergli anche prezzo e tariffa.
			//
			// `lessonTypes` sono le tipologie che il circolo si è scritto, e
			// quelle sono del luogo, non tue: le scrive chi lo gestisce, con
			// una chiamata a parte. Stanno nella stessa bozza perché
			// scriverne una e darle un prezzo è un gesto solo.
			return {
				lessonPrices: settings.lessonPrices ?? {},
				availableLessonTypes: settings.availableLessonTypes ?? {},
				billingData: settings.billingRates ?? {},
				lessonTypes: settings.schoolLessonTypes ?? {},
			}
		case "pagamenti":
			// a chi pagano e come: chi paga il circolo un metodo non ce l'ha,
			// ma è la stessa domanda — come arrivano a te i soldi della lezione
			return {
				availablePaymentMethods: settings.availablePaymentMethods ?? [],
				playersPayTo: settings.playersPayTo ?? PAID_TO_COACH,
			}
		case "tariffe":
			// quanto costa il campo e di chi è il conto: sono la stessa cosa
			// vista da due parti, e si decidono insieme
			return {
				courtsFee: settings.courtsFee ?? {},
				courtPaidBy: settings.courtPaidBy ?? COURT_PAID_BY_COACH,
			}
		default:
			return {}
	}
}

const buildPatch = (draft, original) => {
	const patch = {}

	Object.keys(draft).forEach(field => {
		const next = typeof draft[field] === "string" ? draft[field].trim() : draft[field]
		const previous =
			typeof original[field] === "string" ? original[field].trim() : original[field]

		if (JSON.stringify(next) !== JSON.stringify(previous)) patch[field] = next
	})

	return patch
}

export function SchoolSectionModal({ closeModal, section }) {
	const { coach } = useContext(profileContext)
	const Modal = useContext(modalContext)
	const {
		getSelectedSchool,
		selectedSchool,
		canEditSchool,
		getSchoolSettings,
		updateSchool,
		updateSchoolSettings,
	} = useContext(schoolContext)
	const {
		paymentMethods,
		sports,
		surfaces,
		catalogLessonTypes,
		getPaymentMethod,
		getSport,
	} = useContext(sharedDataContext)

	const school = getSelectedSchool()
	const settings = getSchoolSettings()

	const [draft, setDraft] = useState(() => initialDraft(section, school ?? {}, settings))
	const [isSaving, setIsSaving] = useState(false)
	const [sportID, setSportID] = useState(null)
	// Scrivere una tipologia è una fase di questo modale, non un modulo
	// incastrato in fondo al listino: finché c'è una bozza si vede quella, e
	// Indietro riporta al listino com'era.
	const [draftType, setDraftType] = useState(null)
	// Quale si sta modificando. `null` vuol dire che è nuova: è l'unica
	// differenza tra scriverne una e correggerne una, e la schermata è la
	// stessa.
	const [editedTypeID, setEditedTypeID] = useState(null)
	// il tasto indietro del telefono, mentre la si scrive, fa quello di
	// Indietro: si torna al listino invece di chiudere tutto
	useBackSteps(draftType ? 1 : 0, () => {
		setDraftType(null)
		setEditedTypeID(null)
	})

	if (!school) return null

	const isSchoolSection = SCHOOL_SECTIONS.includes(section)
	// il luogo lo modifica chi lo possiede; le proprie condizioni sempre
	const canEdit = isSchoolSection ? canEditSchool(selectedSchool) : true

	const sportIDs = [
		...new Set([
			...Object.keys(school.courts ?? {}),
			...Object.keys(settings.lessonPrices ?? {}),
			...Object.keys(settings.courtsFee ?? {}),
		]),
	]

	const set = patch => setDraft(prev => ({ ...prev, ...patch }))

	// I tipi di lezione come sono in questo circolo, bozza compresa: una
	// tipologia appena scritta non sta ancora nel catalogo, e senza guardare
	// qui il listino la chiamerebbe col suo identificativo.
	// col nome che si legge, sigla compresa: quella appena accesa si vede già qui
	const shownLessonTypes = withDisplayNames(
		resolveLessonTypes(catalogLessonTypes, draft.lessonTypes),
	)
	const getLessonType = lessonTypeID => shownLessonTypes[lessonTypeID] ?? null

	// Una tipologia del circolo si cambia tutta — è sua. Una del catalogo si
	// corregge e basta: come si chiama e cos'è è uguale per tutti.
	const isOwnType = lessonTypeID =>
		!catalogLessonTypes[lessonTypeID] || isOwnLessonType(draft.lessonTypes?.[lessonTypeID])

	const closeDraftType = () => {
		setDraftType(null)
		setEditedTypeID(null)
	}

	const createType = () => {
		setEditedTypeID(null)
		setDraftType(EMPTY_DRAFT_TYPE)
	}

	const editType = lessonTypeID => {
		setEditedTypeID(lessonTypeID)
		setDraftType(draftFromLessonType(getLessonType(lessonTypeID)))
	}

	// La tipologia entra nella bozza — definizione e lista insieme — e va su
	// Firestore col salvataggio del listino, accanto al suo prezzo. Così
	// Indietro non lascia in giro niente, e Annulla annulla anche lei.
	const saveDraftType = () => {
		const isNew = editedTypeID === null
		const lessonTypeID = editedTypeID ?? newSchoolLessonTypeID(draftType.lessonTypeName)
		const lessonTypes = { ...draft.lessonTypes }

		if (isNew || isOwnType(lessonTypeID)) {
			lessonTypes[lessonTypeID] = buildSchoolLessonType(draftType)
		} else {
			// Una correzione che non corregge più niente si toglie: la
			// tipologia torna quella di tutti, e sul documento del circolo
			// non resta una riga che non dice niente.
			const adjustment = buildLessonTypeAdjustment(
				draftType,
				catalogLessonTypes[lessonTypeID],
			)

			if (adjustment) lessonTypes[lessonTypeID] = adjustment
			else delete lessonTypes[lessonTypeID]
		}

		// Una tipologia nuova la si scrive per farla: entra in lista subito,
		// altrimenti bisognerebbe andarla a ripescare tra quelle spente.
		const active = getSportLessonTypes(
			{ availableLessonTypes: draft.availableLessonTypes },
			shownSport,
			getSport(shownSport),
			getLessonType,
		)

		set({
			lessonTypes,
			...(isNew
				? {
						availableLessonTypes: {
							...draft.availableLessonTypes,
							[shownSport]: [...active, lessonTypeID],
						},
					}
				: {}),
		})
		closeDraftType()
	}

	// Nei campi gli sport sono quelli che stai scrivendo; nel listino e nelle
	// tariffe sono quelli che al circolo esistono già.
	const sectionSportIDs = section === "campi" ? Object.keys(draft.courts ?? {}) : sportIDs
	const shownSport = sportID && sectionSportIDs.includes(sportID) ? sportID : sectionSportIDs[0]

	// Un campo si aggiunge solo dove ci si può fare lezione: finché la
	// registrazione dà il solo tennis (`DEFAULT_SPORT`), un campo da padel
	// resterebbe un campo su cui nessuno può segnare niente. I campi di altri
	// sport già scritti restano, e si continuano a vedere e cambiare.
	const addableSportIDs =
		section === "campi" &&
		canEdit &&
		sports[DEFAULT_SPORT] &&
		!sectionSportIDs.includes(DEFAULT_SPORT)
			? [DEFAULT_SPORT]
			: []

	const addSport = newSportID => {
		const firstSurface = Object.values(surfaces)[0]
		if (!firstSurface) return

		set({
			courts: {
				...draft.courts,
				[newSportID]: {
					1: {
						surfaceType: firstSurface.surfaceType,
						surfaceName: firstSurface.surfaceName,
					},
				},
			},
		})
		setSportID(newSportID)
	}

	const runSave = async saving => {
		setIsSaving(true)
		try {
			await saving()
			toast.success("Salvato!", { position: "top-center" })
			closeModal()
		} catch (err) {
			toast.error(err.message || "Errore durante il salvataggio", {
				position: "top-center",
			})
			setIsSaving(false)
		}
	}

	const handleSave = () => {
		const patch = buildPatch(draft, initialDraft(section, school, settings))

		if (Object.keys(patch).length === 0) {
			closeModal()
			return
		}

		if (isSchoolSection) return runSave(() => updateSchool(selectedSchool, patch))

		// Le tipologie sono del circolo e le scrive chi lo gestisce; prezzi,
		// tariffe e liste sono tue. Due chiamate, e in quest'ordine: il
		// listino cita le tipologie, quindi devono esserci già quando arriva.
		const { lessonTypes: draftLessonTypes, ...ownPatch } = patch

		return runSave(async () => {
			if (draftLessonTypes) {
				await updateSchool(selectedSchool, { lessonTypes: draftLessonTypes })
			}
			if (Object.keys(ownPatch).length > 0) {
				await updateSchoolSettings(selectedSchool, ownPatch)
			}
		})
	}

	// Tornare a usare i valori comuni del circolo: è il modo di dire "su
	// questo non ho condizioni mie".
	const handleInherit = () =>
		runSave(() =>
			updateSchoolSettings(
				selectedSchool,
				Object.fromEntries(SETTINGS_FIELDS[section].map(field => [field, null])),
			),
		)

	// Si torna al circolo se qui c'è qualcosa di tuo e il circolo ha un
	// valore comune da darti al suo posto.
	const canInherit =
		!isSchoolSection &&
		SETTINGS_FIELDS[section].some(
			field => isOwnSetting(coach, selectedSchool, field) && school[field] !== undefined,
		)

	const body = () => {
		switch (section) {
			case "dati":
				return (
					<>
						{/* Il logo ha un foglio suo (PictureModal), che si salva da
						    solo: qui si vede, e da qui ci si va. Finito lì si torna
						    qui. */}
						<Paragraph title="Logo">
							<Div d="flex" align="center" m={{ y: "0.5rem" }} style={{ gap: "1rem" }}>
								<SchoolAvatar school={school} size={56} />
								{canEdit ? (
									<Text
										tag="button"
										type="button"
										textSize="caption"
										textColor={theme.colors.primary}
										cursor="pointer"
										onClick={() => Modal.open("picture", { kind: "school", back: "dati" })}
										style={{ border: 0, background: "none", padding: 0, font: "inherit" }}>
										{school.logo ? "Cambia il logo" : "Aggiungi il logo"}
									</Text>
								) : (
									<Text textSize="caption" textColor={theme.colors.text_light}>
										{school.logo ? "Il logo del circolo" : "Il circolo non ha un logo"}
									</Text>
								)}
							</Div>
						</Paragraph>
						<Paragraph title="Nome">
							<TextInput
								value={draft.schoolName}
								disabled={!canEdit}
								handleChange={e => set({ schoolName: e.target.value })}
							/>
						</Paragraph>
						<Paragraph title="Indirizzo">
							<TextInput
								value={draft.address}
								disabled={!canEdit}
								placeholder="via, numero, città"
								handleChange={e => set({ address: e.target.value })}
							/>
						</Paragraph>
						<Paragraph title="Nome su Google Maps">
							<TextInput
								value={draft.mapsName}
								disabled={!canEdit}
								placeholder="solo se cercando il nome Maps sbaglia posto"
								handleChange={e => set({ mapsName: e.target.value })}
							/>
						</Paragraph>

						{/* Come finisce il tuo rapporto con questo circolo: chi
						    l'ha creato lo chiude, chi ci lavora se ne va. Sono
						    la stessa cosa detta dai due lati, quindi stanno
						    nello stesso posto — qui, dove vive la sua identità
						    — e non se ne vedono mai due insieme. */}
						{school.ownerCoachID === coach?.coachID ? (
							<Div
								d="flex"
								align="center"
								m={{ t: "1.5rem" }}
								cursor="pointer"
								style={{ gap: "0.5rem" }}
								onClick={() => Modal.open("deleteSchool")}>
								<FontAwesomeIcon icon={faTrash} color={theme.colors.red} size="sm" />
								<Text textSize="caption" textColor={theme.colors.red}>
									Chiudi questo circolo
								</Text>
							</Div>
						) : (
							<Div
								d="flex"
								align="center"
								m={{ t: "1.5rem" }}
								cursor="pointer"
								style={{ gap: "0.5rem" }}
								onClick={() => Modal.open("leaveSchool")}>
								<FontAwesomeIcon
									icon={faRightFromBracket}
									color={theme.colors.text_light}
									size="sm"
								/>
								<Text textSize="caption" textColor={theme.colors.text_light}>
									Esci da questo circolo
								</Text>
							</Div>
						)}
					</>
				)
			case "campi":
				return (
					<SchoolCourtsEditor
						sportID={shownSport}
						courts={draft.courts}
						surfaces={surfaces}
						isEditing={canEdit}
						onChange={courts => set({ courts })}
					/>
				)
			case "prezzi":
				return (
					<LessonTypesEditor
						sportID={shownSport}
						sport={getSport(shownSport)}
						prices={draft.lessonPrices}
						billingRates={draft.billingData}
						availableLessonTypes={draft.availableLessonTypes}
						schoolLessonTypes={draft.lessonTypes}
						lessonTypes={catalogLessonTypes}
						getLessonType={getLessonType}
						canEditTypes={canEditSchool(selectedSchool)}
						onChangePrices={lessonPrices => set({ lessonPrices })}
						onChangeTypes={availableLessonTypes => set({ availableLessonTypes })}
						onChangeBilling={billingData => set({ billingData })}
						onCreateType={createType}
						onEditType={editType}
					/>
				)
			case "pagamenti":
				return (
					<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }} m={{ b: "1rem" }}>
						{Object.keys(paymentMethods).map(methodID => (
							<ToggleChip
								key={methodID}
								size="lg"
								text={getPaymentMethod(methodID)?.paymentName ?? methodID}
								isSelected={draft.availablePaymentMethods.includes(methodID)}
								onClick={() =>
									set({
										availablePaymentMethods:
											draft.availablePaymentMethods.includes(methodID)
												? draft.availablePaymentMethods.filter(
														id => id !== methodID,
													)
												: [...draft.availablePaymentMethods, methodID],
									})
								}
							/>
						))}
					</Div>
				)
			case "tariffe":
				return (
					<CourtsFeeEditor
						sportID={shownSport}
						fees={draft.courtsFee}
						isEditing={true}
						onChange={courtsFee => set({ courtsFee })}
					/>
				)
			default:
				return null
		}
	}

	// Una bozza aperta prende tutto il modale: una cosa alla volta, come il
	// resto dell'app.
	if (draftType) {
		return (
			<LessonTypeForm
				lessonTypeID={editedTypeID}
				draftType={draftType}
				catalogType={editedTypeID ? catalogLessonTypes[editedTypeID] : null}
				isNew={editedTypeID === null}
				isOwn={editedTypeID === null || isOwnType(editedTypeID)}
				onChange={setDraftType}
				onBack={closeDraftType}
				onAdd={saveDraftType}
			/>
		)
	}

	return (
		<Container>
			<Div
				d="flex"
				justify="space-between"
				align="center"
				m={{ b: "0.5rem" }}
				textWeight="bold"
				textSize="title">
				{TITLES[section]}
			</Div>

			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				{isSchoolSection ? school.schoolName : `Le tue condizioni a ${school.schoolName}`}
			</Text>

			{/* il selettore resta fermo: scorre solo quello che sta guardando */}
			{PER_SPORT_SECTIONS.includes(section) && (
				<Container>
					<SportSelector
						sportIDs={sectionSportIDs}
						selected={shownSport}
						onSelect={setSportID}
						addableSportIDs={addableSportIDs}
						onAdd={addSport}
						getSport={getSport}
					/>
				</Container>
			)}

			<Div maxH="60vh" overflow="auto">
				{/* chi paga il campo vale per il circolo, non per uno sport:
				    resta fermo mentre sotto scorrono le tariffe */}
				{section === "tariffe" && (
					<Container>
						<CourtPaidByEditor
							courtPaidBy={draft.courtPaidBy}
							onChange={courtPaidBy => set({ courtPaidBy })}
						/>
					</Container>
				)}
				{section === "pagamenti" && (
					<Container>
						<PlayersPayToEditor
							playersPayTo={draft.playersPayTo}
							onChange={playersPayTo => set({ playersPayTo })}
						/>
					</Container>
				)}
				<Container>
					<StepSwap value={`${section}_${shownSport ?? ""}`}>
						{body()}
					</StepSwap>
				</Container>
			</Div>

			{!canEdit && (
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.5rem" }}>
					Il circolo è di qualcun altro: qui puoi solo guardare.
				</Text>
			)}

			{canInherit && (
				<Div
					textAlign="center"
					m={{ t: "0.75rem" }}
					cursor="pointer"
					onClick={() => !isSaving && handleInherit()}>
					<Text textSize="caption" textColor={theme.colors.primary}>
						Usa i valori del circolo
					</Text>
				</Div>
			)}

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							textSize="subheader"
							text={canEdit ? "Annulla" : "Chiudi"}
							disabled={isSaving}
							action={closeModal}
						/>
					</Col>
					{canEdit && (
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={theme.colors.gunmetal_fill}
								textColor="white"
								textSize="subheader"
								text={isSaving ? "Salvo..." : "Salva"}
								disabled={isSaving}
								action={handleSave}
							/>
						</Col>
					)}
				</Row>
			</Div>
		</Container>
	)
}

export default SchoolSectionModal
